import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import jsQR from 'jsqr'
import { Button, Header, Icon, Screen, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { eventAccessProblem } from '@utils'
import { useEventById } from '@hooks'
import type { EventsStackParamList } from '../../../navigation/navigator/EventsStackNavigator'
import { AccessDenied } from '../components/AccessDenied'
import { OfflineBanner } from './components/OfflineBanner'
import { ScanResult } from './components/ScanResult'
import { useCheckInScanController } from './components/useCheckInScanController'

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>
}
type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike

type Props = NativeStackScreenProps<EventsStackParamList, 'EventCheckInScan'>

/**
 * Full-screen scan: reads a ticket QR or a personal KeepTouch QR, shows the
 * result at once, and lets the organizer chain scans without going back. When
 * no camera is available (permission refused, none in the test environment),
 * a manual "paste the QR content" fallback takes over the same handler.
 */
export const EventCheckInScan: React.FC<Props> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const { eventId } = route.params
  const { data: detail, error: detailError } = useEventById(eventId)
  const {
    result,
    isResultShowing,
    handleCode,
    continueScanning,
    onRegister,
    isScanning,
    isRegistering,
    isOnline,
    pendingCount,
  } = useCheckInScanController(eventId)

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [cameraActive, setCameraActive] = useState(false)
  const [cameraUnavailable, setCameraUnavailable] = useState(false)
  const [manualCode, setManualCode] = useState('')

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    setCameraActive(false)
  }, [])

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        void videoRef.current.play()
        setCameraActive(true)
      }
    } catch {
      setCameraUnavailable(true)
    }
  }, [])

  useEffect(() => {
    void startCamera()
    return () => stopCamera()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scanWithJsQR = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      const imageData = ctx.getImageData(0, 0, width, height)
      const decoded = jsQR(imageData.data, width, height)
      if (decoded?.data) handleCode(decoded.data)
    },
    [handleCode],
  )

  const scanFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    const BarcodeDetectorCtor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor })
      .BarcodeDetector
    if (BarcodeDetectorCtor) {
      const detector = new BarcodeDetectorCtor({ formats: ['qr_code'] })
      void detector
        .detect(canvas)
        .then((barcodes) => {
          if (barcodes.length > 0 && barcodes[0].rawValue) handleCode(barcodes[0].rawValue)
        })
        .catch(() => scanWithJsQR(ctx, canvas.width, canvas.height))
    } else {
      scanWithJsQR(ctx, canvas.width, canvas.height)
    }
  }, [handleCode, scanWithJsQR])

  useEffect(() => {
    if (cameraActive && !isResultShowing) {
      scanIntervalRef.current = setInterval(scanFrame, 500)
    }
    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current)
        scanIntervalRef.current = null
      }
    }
  }, [cameraActive, isResultShowing, scanFrame])

  const submitManualCode = () => {
    const code = manualCode.trim()
    if (!code) return
    handleCode(code)
    setManualCode('')
  }

  const accessProblem = eventAccessProblem(detail, detailError)
  if (accessProblem) {
    return (
      <AccessDenied
        message={t(`events.access.${accessProblem === 'notFound' ? 'notFound' : 'forbidden'}`)}
        onBack={() => navigation.navigate('EventsList')}
      />
    )
  }

  return (
    <Screen noPaddingTop>
      <Header
        title={t('events.checkin.scan.headerTitle')}
        iconLeft={Platform.OS === 'web' ? 'arrowLeft' : 'arrowBack'}
        onPressLeft={() => {
          stopCamera()
          navigation.navigate('EventCheckIn', { eventId })
        }}
      />
      <View style={styles.page}>
        <OfflineBanner isOnline={isOnline} pendingCount={pendingCount} />
        {result ? (
          <ScanResult
            result={result}
            registering={isRegistering}
            onRegister={onRegister}
            onContinue={continueScanning}
          />
        ) : cameraUnavailable ? (
          <View style={styles.manual}>
            <Icon name="scan" size={64} color={theme.colors.greyBlue} />
            <Text type="bodyBold" color={theme.colors.primary} center style={styles.manualText}>
              {t('events.checkin.scan.cameraUnavailable')}
            </Text>
            <TextInput
              testID="scan-manual-input"
              placeholder={t('events.checkin.scan.manualPlaceholder')}
              value={manualCode}
              onChangeText={setManualCode}
              autoCapitalize="none"
              containerStyle={styles.manualInput}
            />
            <Button
              title={t('events.checkin.scan.manualSubmit')}
              testID="scan-manual-submit"
              disabled={!manualCode.trim() || isScanning}
              onPress={submitManualCode}
            />
          </View>
        ) : (
          <View style={styles.camera}>
            {isScanning ? <ActivityIndicator color={theme.colors.primary} /> : null}
            <video
              ref={videoRef}
              style={{ width: '100%', maxWidth: 500, borderRadius: 16, objectFit: 'cover' }}
              autoPlay
              playsInline
              muted
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.instruction}>
              {t('events.checkin.scan.instruction')}
            </Text>
          </View>
        )}
      </View>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    page: {
      flex: 1,
      width: '100%',
      maxWidth: 520,
      alignSelf: 'center',
      paddingHorizontal: theme.spacing.lg,
    },
    camera: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: theme.spacing.md },
    instruction: { textAlign: 'center', paddingHorizontal: theme.spacing.lg },
    manual: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.lg,
      paddingVertical: theme.spacing.xxl,
    },
    manualText: { paddingHorizontal: theme.spacing.lg },
    manualInput: { width: '100%' },
  })
